/**
 * 🎯 Enhanced Users Tab - Admin Panel
 * 
 * Complete user management interface with CMT invitation system,
 * user status management, and comprehensive audit trail.
 */

import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Typography, 
  Tooltip, 
  Avatar, 
  Modal, 
  Alert,
  Dropdown,
  Menu,
  Input,
  Select,
  Switch,
  Divider,
  Descriptions
} from 'antd';
import { 
  UserAddOutlined, 
  MoreOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  MailOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  SearchOutlined,
  FilterOutlined,
  UserOutlined,
  SettingOutlined,
  AuditOutlined
} from '@ant-design/icons';
import { message } from 'antd';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  orderBy 
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import CMTInvitationModal from './CMTInvitationModal';
import cmtInvitationService from '../../services/cmtInvitationService';
import cmtInvitationTransactions from '../../services/cmtInvitationTransactions';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const EnhancedUsersTab = ({ selectedOrgId, selectedOrgName, selectedOrgData }) => {
  const { currentUser } = useAuth();
  
  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Modal states
  const [invitationModalVisible, setInvitationModalVisible] = useState(false);
  const [userDetailModalVisible, setUserDetailModalVisible] = useState(false);
  const [deactivateModalVisible, setDeactivateModalVisible] = useState(false);
  
  // User actions
  const [actionLoading, setActionLoading] = useState(false);

  // Load users when organization changes
  useEffect(() => {
    if (selectedOrgId) {
      loadUsers();
    }
  }, [selectedOrgId]);

  /**
   * 📥 Load all users for the selected organization
   */
  const loadUsers = async () => {
    try {
      setLoading(true);
      
      const usersRef = collection(db, 'c3_users');
      const q = query(
        usersRef, 
        where('orgId', '==', selectedOrgId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const userList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setUsers(userList);
      
    } catch (error) {
      console.error('Error loading users:', error);
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 🔄 Update user status (active/deactivated)
   */
  const updateUserStatus = async (userId, newStatus) => {
    try {
      setActionLoading(true);
      
      const userRef = doc(db, 'c3_users', userId);
      await updateDoc(userRef, {
        status: newStatus,
        updatedAt: new Date(),
        lastUpdatedBy: currentUser.uid
      });
      
      message.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      await loadUsers(); // Refresh the list
      
    } catch (error) {
      console.error('Error updating user status:', error);
      message.error('Failed to update user status');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * 🗑️ Delete user account
   */
  const deleteUser = async (userId) => {
    Modal.confirm({
      title: 'Delete User Account',
      content: 'Are you sure you want to permanently delete this user account? This action cannot be undone.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setActionLoading(true);
          
          const userRef = doc(db, 'c3_users', userId);
          await deleteDoc(userRef);
          
          message.success('User deleted successfully');
          await loadUsers();
          
        } catch (error) {
          console.error('Error deleting user:', error);
          message.error('Failed to delete user');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  /**
   * 📧 Resend invitation email
   */
  const resendInvitation = async (user) => {
    try {
      setActionLoading(true);
      
      // Get user's primary CMT assignment
      const primaryAssignment = user.cmtAssignments?.find(assignment => assignment.isActive);
      
      if (primaryAssignment) {
        await cmtInvitationService.sendInvitationEmail(
          user.email,
          { fullName: user.fullName, cmtRole: primaryAssignment.cmtRole },
          currentUser.fullName || currentUser.email,
          'CMT', // Would need to lookup CMT name
          selectedOrgName
        );
        
        message.success('Invitation email resent successfully');
      } else {
        message.error('User has no active CMT assignments');
      }
      
    } catch (error) {
      console.error('Error resending invitation:', error);
      message.error('Failed to resend invitation');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * 🎨 Get status color and icon
   */
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'active':
        return { color: 'green', icon: <CheckCircleOutlined />, text: 'Active' };
      case 'invited':
        return { color: 'blue', icon: <ClockCircleOutlined />, text: 'Invited' };
      case 'deactivated':
        return { color: 'red', icon: <StopOutlined />, text: 'Deactivated' };
      default:
        return { color: 'gray', icon: <ExclamationCircleOutlined />, text: 'Unknown' };
    }
  };

  /**
   * 🎨 Format CMT assignments for display
   */
  const formatCMTAssignments = (cmtAssignments) => {
    if (!cmtAssignments || cmtAssignments.length === 0) {
      return <Text type="secondary">No CMT assignments</Text>;
    }

    const activeAssignments = cmtAssignments.filter(assignment => assignment.isActive);
    
    return (
      <Space direction="vertical" size="small">
        {activeAssignments.map((assignment, index) => (
          <Tag key={index} color="blue" icon={<TeamOutlined />}>
            {assignment.cmtRole}
          </Tag>
        ))}
      </Space>
    );
  };

  /**
   * 📋 Generate user action menu
   */
  const getUserActionMenu = (user) => (
    <Menu>
      <Menu.Item 
        key="view" 
        icon={<UserOutlined />}
        onClick={() => {
          setSelectedUser(user);
          setUserDetailModalVisible(true);
        }}
      >
        View Details
      </Menu.Item>
      
      <Menu.Item 
        key="edit" 
        icon={<EditOutlined />}
        onClick={() => {
          // Implement edit functionality
          message.info('Edit functionality coming soon');
        }}
      >
        Edit User
      </Menu.Item>
      
      <Menu.Divider />
      
      {user.status === 'invited' && (
        <Menu.Item 
          key="resend" 
          icon={<MailOutlined />}
          onClick={() => resendInvitation(user)}
        >
          Resend Invitation
        </Menu.Item>
      )}
      
      <Menu.Item 
        key="toggle-status" 
        icon={user.status === 'active' ? <StopOutlined /> : <CheckCircleOutlined />}
        onClick={() => updateUserStatus(
          user.id, 
          user.status === 'active' ? 'deactivated' : 'active'
        )}
      >
        {user.status === 'active' ? 'Deactivate' : 'Activate'}
      </Menu.Item>
      
      <Menu.Divider />
      
      <Menu.Item 
        key="delete" 
        icon={<DeleteOutlined />}
        danger
        onClick={() => deleteUser(user.id)}
      >
        Delete User
      </Menu.Item>
    </Menu>
  );

  /**
   * 🔍 Filter users based on search and filters
   */
  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    
    const matchesRole = roleFilter === 'all' || 
      user.cmtAssignments?.some(assignment => 
        assignment.isActive && assignment.cmtRole === roleFilter
      );
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  /**
   * 📊 Table columns configuration
   */
  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, user) => (
        <Space>
          <Avatar icon={<UserOutlined />} size="large" />
          <div>
            <div style={{ fontWeight: 500 }}>{user.fullName || 'Unnamed User'}</div>
            <Text type="secondary">{user.email}</Text>
          </div>
        </Space>
      ),
      sorter: (a, b) => (a.fullName || '').localeCompare(b.fullName || ''),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const display = getStatusDisplay(status);
        return (
          <Tag color={display.color} icon={display.icon}>
            {display.text}
          </Tag>
        );
      },
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Invited', value: 'invited' },
        { text: 'Deactivated', value: 'deactivated' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'CMT Roles',
      key: 'cmtRoles',
      render: (_, user) => formatCMTAssignments(user.cmtAssignments),
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => {
        if (!createdAt) return 'Unknown';
        const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
        return date.toLocaleDateString();
      },
      sorter: (a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateA - dateB;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, user) => (
        <Dropdown 
          overlay={getUserActionMenu(user)} 
          trigger={['click']}
          placement="bottomRight"
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px 0' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              <TeamOutlined /> User Management
            </Title>
            <Text type="secondary">
              Manage user accounts, roles, and permissions for {selectedOrgName}
            </Text>
          </div>
          
          <Button 
            type="primary" 
            icon={<UserAddOutlined />}
            onClick={() => setInvitationModalVisible(true)}
            size="large"
          >
            Invite User
          </Button>
        </div>

        {/* Filters */}
        <Card size="small">
          <Space wrap>
            <Search
              placeholder="Search users..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
              placeholder="Filter by status"
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="invited">Invited</Option>
              <Option value="deactivated">Deactivated</Option>
            </Select>
            
            <Select
              value={roleFilter}
              onChange={setRoleFilter}
              style={{ width: 150 }}
              placeholder="Filter by role"
            >
              <Option value="all">All Roles</Option>
              <Option value="Leader">Leader</Option>
              <Option value="Admin">Admin</Option>
              <Option value="Member">Member</Option>
              <Option value="Observer">Observer</Option>
            </Select>
          </Space>
        </Card>
      </div>

      {/* Summary Stats */}
      <div style={{ marginBottom: 24 }}>
        <Space size="large">
          <div>
            <Text strong>Total Users: </Text>
            <Text>{users.length}</Text>
          </div>
          <div>
            <Text strong>Active: </Text>
            <Text style={{ color: '#52c41a' }}>
              {users.filter(u => u.status === 'active').length}
            </Text>
          </div>
          <div>
            <Text strong>Invited: </Text>
            <Text style={{ color: '#1890ff' }}>
              {users.filter(u => u.status === 'invited').length}
            </Text>
          </div>
          <div>
            <Text strong>Deactivated: </Text>
            <Text style={{ color: '#ff4d4f' }}>
              {users.filter(u => u.status === 'deactivated').length}
            </Text>
          </div>
        </Space>
      </div>

      {/* Users Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} users`,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* CMT Invitation Modal */}
      <CMTInvitationModal
        visible={invitationModalVisible}
        onCancel={() => setInvitationModalVisible(false)}
        onSuccess={() => {
          loadUsers(); // Refresh user list
          setInvitationModalVisible(false);
        }}
        selectedOrgId={selectedOrgId}
        selectedOrgName={selectedOrgName}
      />

      {/* User Detail Modal */}
      <Modal
        title="User Details"
        open={userDetailModalVisible}
        onCancel={() => setUserDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setUserDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedUser && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Full Name">
              {selectedUser.fullName || 'Not provided'}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {selectedUser.email}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {getStatusDisplay(selectedUser.status).text}
            </Descriptions.Item>
            <Descriptions.Item label="User ID">
              {selectedUser.id}
            </Descriptions.Item>
            <Descriptions.Item label="Organization ID">
              {selectedUser.orgId}
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {selectedUser.createdAt?.toDate ? 
                selectedUser.createdAt.toDate().toLocaleString() : 
                'Unknown'
              }
            </Descriptions.Item>
            <Descriptions.Item label="CMT Assignments" span={2}>
              {selectedUser.cmtAssignments && selectedUser.cmtAssignments.length > 0 ? (
                <div>
                  {selectedUser.cmtAssignments.map((assignment, index) => (
                    <Tag 
                      key={index} 
                      color={assignment.isActive ? 'green' : 'gray'}
                      style={{ marginBottom: 4 }}
                    >
                      {assignment.cmtRole} {!assignment.isActive && '(Inactive)'}
                    </Tag>
                  ))}
                </div>
              ) : (
                <Text type="secondary">No CMT assignments</Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default EnhancedUsersTab;
